import { useEffect, useRef, useState } from 'preact/hooks';
import { rpx } from 'client/lib/rpx-client';
import { showError } from '@components/app-error';

type Props = {
  courseId: UUID;
  sessionId: UUID;
  value: string;
  isCustomized: boolean;
  isDisrupted: boolean;
  onSaved(): void;
};

export function TopicCell({
  courseId,
  sessionId,
  value,
  isCustomized,
  isDisrupted,
  onSaved,
}: Props) {
  const trimmed = value.trim();
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const lastSavedRef = useRef(trimmed);

  useEffect(() => {
    // Only sync the draft from props when the user has no unsaved edits.
    // Otherwise reloading the parent state would clobber an in-progress topic.
    if (draft.trim() === lastSavedRef.current) {
      setDraft(value);
    }
    lastSavedRef.current = trimmed;
  }, [trimmed, sessionId]);

  async function commit() {
    const next = draft.trim();
    if (next === lastSavedRef.current) {
      return;
    }
    setSaving(true);
    try {
      await rpx.meetingSeries.setSeriesSessionTopic({
        courseId,
        sessionId,
        topic: next,
      });
      lastSavedRef.current = next;
      onSaved();
    } catch (err) {
      showError(err);
      setDraft(lastSavedRef.current);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div class="flex items-center gap-2">
      <input
        type="text"
        class="inline-ruz-input flex-1 text-sm py-1"
        value={draft}
        placeholder="Click to add topic…"
        disabled={isDisrupted}
        onInput={(e: any) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e: any) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            (e.target as HTMLInputElement).blur();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            setDraft(lastSavedRef.current);
            (e.target as HTMLInputElement).blur();
          }
        }}
      />
      {isCustomized && !saving && (
        <span class="text-xs text-gray-500 whitespace-nowrap">customized</span>
      )}
      {saving && <span class="text-xs text-gray-400">saving…</span>}
    </div>
  );
}
